import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import BackButton from '@/components/ui/BackButton/BackButton.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import EditIcon from '@mui/icons-material/Edit';
import useRestaurants from '@/api/useRestaurants.ts';
import { Schema, schema } from '@/components/modals/Integration/AddIntegrationModal.tsx';
import useIntegrationEdit from '@/api/useIntegrationEdit.ts';
import { allIntegrationPlatforms, IntegrationPlatform as IntegrationPlatformT } from '@/_constants/IntegrationPlatform';
import IntegrationPlatform from '@/_constants/IntegrationPlatform.ts';
import useIntegrations from '@/api/useIntegrations.ts';

type EditIntegrationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  integrationId: number;
};
function EditIntegrationModal({ isOpen, onClose, onConfirm, integrationId }: EditIntegrationModalProps) {
  const { t } = useTranslation();
  const { data: restaurants, isLoading: isLoadingRestaurants } = useRestaurants();
  const {
    mutateAsync: editIntegration,
    isError: isErrorEditFaq,
    isPending: isLoadingEditFaq,
    isSuccess: isSuccessEditFaq,
    error: errorEditFaq,
  } = useIntegrationEdit();

  const {
    data: currentData,
    isLoading: isLoadingCurrentData,
    isSuccess,
  } = useIntegrations(true, {
    id: integrationId,
  });

  const { handleSubmit, control, setValue, watch } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      restaurant_id: '',
      platform_name: '' as unknown as IntegrationPlatformT,
      api_key: '',
      secret_token: '',
      venue_group_id: '',
      venue_id: '',
    },
  });

  const handleUpdateReservation = ({ restaurant_id, ...rest }: Schema) => {
    void (async () => {
      try {
        if (isLoadingEditFaq || isLoadingRestaurants || !currentData || !currentData[0]) {
          return;
        }

        await editIntegration({
          ...rest,
          id: integrationId,
          restaurant_id: parseInt(restaurant_id),
        });

        if (onConfirm) {
          onConfirm();
        }

        onClose();
      } catch (error) {
        console.error('Error editing integration: ', error);
      }
    })();
  };

  useEffect(() => {
    if (isSuccess && currentData[0]) {
      const { restaurant_id, platform_name, api_key, secret_token, venue_group_id, venue_id } = currentData[0];
      setValue('restaurant_id', `${restaurant_id.id}`);
      setValue('platform_name', platform_name ?? '');
      setValue('api_key', api_key ?? '');
      setValue('secret_token', secret_token ?? '');
      setValue('venue_group_id', venue_group_id ?? '');
      setValue('venue_id', venue_id ?? '');
    }
  }, [currentData, isLoadingCurrentData, isSuccess, setValue]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <form className="modal-form" onSubmit={handleSubmit(handleUpdateReservation)}>
        <DialogTitle>{t('modals.editIntegrationModal.title')}</DialogTitle>
        <DialogContent>
          <LoaderWrapper isLoading={isLoadingCurrentData || isLoadingRestaurants}>
            <div className="pt-2 flex flex-col gap-4">
              <div className="flex flex-col gap-2 empty:hidden">
                {isSuccessEditFaq && (
                  <Alert severity="success">{t('modals.editIntegrationModal.successMessage')}</Alert>
                )}
                {isErrorEditFaq && <Alert severity="error">{errorEditFaq?.message as string}</Alert>}
              </div>
              <div className="flex flex-col gap-4 w-full">
                <Controller
                  control={control}
                  name="restaurant_id"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <FormControl error={!!errors.restaurant_id}>
                      <InputLabel shrink id="restaurant_id">
                        {t('label.restaurant')}
                      </InputLabel>
                      <Select
                        value={value}
                        onChange={(event) => onChange(`${event.target.value}`)}
                        displayEmpty
                        variant="outlined"
                        label={t('label.restaurant')}
                        labelId="restaurant_id"
                        renderValue={(currentValue) =>
                          currentValue == '' ? (
                            <span className="text-[#a1a2a1]">{t('placeholder.restaurant')}</span>
                          ) : (
                            restaurants?.find((restaurant) => restaurant.id === parseInt(currentValue))?.name
                          )
                        }
                      >
                        {restaurants?.map((restaurant) => (
                          <MenuItem key={`restaurant_${restaurant.id}`} value={restaurant.id}>
                            {restaurant.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.restaurant_id?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="platform_name"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <FormControl error={!!errors.platform_name}>
                      <InputLabel shrink id="platform_name">
                        {t('label.integration')}
                      </InputLabel>
                      <Select
                        value={value}
                        onChange={(event) => onChange(`${event.target.value}`)}
                        displayEmpty
                        variant="outlined"
                        label={t('label.integration')}
                        labelId="platform_name"
                        renderValue={(currentValue) =>
                          !currentValue ? (
                            <span className="text-[#a1a2a1]">{t('placeholder.integration')}</span>
                          ) : (
                            currentValue
                          )
                        }
                      >
                        {allIntegrationPlatforms.map((integration) => (
                          <MenuItem key={`integration_${integration}`} value={integration}>
                            {integration}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.platform_name?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="api_key"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.api_key}
                      helperText={errors.api_key?.message}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.api_key')}
                      placeholder={t('placeholder.api_key')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="secret_token"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.secret_token}
                      helperText={errors.secret_token?.message}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.secret_token')}
                      placeholder={t('placeholder.secret_token')}
                    />
                  )}
                />
                {watch('platform_name') === IntegrationPlatform.SEVEN_ROOMS ? (
                  <>
                    <Controller
                      control={control}
                      name="venue_group_id"
                      render={({ field: { onChange, value }, formState: { errors } }) => (
                        <TextField
                          error={!!errors.venue_group_id}
                          helperText={errors.venue_group_id?.message}
                          value={value}
                          onChange={onChange}
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          variant="outlined"
                          label={t('label.venue_group_id')}
                          placeholder={t('placeholder.venue_group_id')}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="venue_id"
                      render={({ field: { onChange, value }, formState: { errors } }) => (
                        <TextField
                          error={!!errors.venue_id}
                          helperText={errors.venue_id?.message}
                          value={value}
                          onChange={onChange}
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          variant="outlined"
                          label={t('label.venue_id')}
                          placeholder={t('placeholder.venue_id')}
                        />
                      )}
                    />
                  </>
                ) : null}
              </div>
            </div>
          </LoaderWrapper>
        </DialogContent>
        <DialogActions>
          <div className="flex flex-wrap grow p-4 pt-0 justify-between">
            <BackButton onClick={onClose} autoFocus />
            <LoadingButton
              startIcon={<EditIcon />}
              color="primary"
              variant="contained"
              loading={isLoadingEditFaq}
              type="submit"
            >
              {t('button.update')}
            </LoadingButton>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EditIntegrationModal;
